import React, { useState, useRef } from 'react';
import { DB, uploadImage } from '../data/database';
import { CheckIcon } from './Icons';
import { logActivity, Notifications } from '../data/notifications';
import centralUpdateStatus, { detectUnitType } from '../utils/updateUnitStatus';
import { convertCurrency, calculateContainerTotalJPY } from '../utils/currency';

function ContainerSeal({ container, user, onUpdate }) {
  const [sealNumber, setSealNumber] = useState(container.sealNumber || '');
  const [sealPhoto, setSealPhoto]   = useState('');
  const [fullPhoto, setFullPhoto]   = useState('');
  const [uploading, setUploading]   = useState(false);
  const sealRef = useRef();
  const fullRef = useRef();

  const allUnits = [...(DB.get('vehicles') || []), ...(DB.get('engines') || []), ...(DB.get('spareParts') || [])];
  const units = (container.units || []).map(uid => allUnits.find(u => u.id === uid)).filter(Boolean);
  const unitsData = container.unitsData || [];
  const loadedCount = units.filter(u => unitsData.find(d => d.unit_id === u.id && d.loaded_at)).length;
  const allLoaded = units.length > 0 && loadedCount === units.length;

  if (container.status !== 'loading' || !allLoaded) return null;

  const compressAndUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) return '';
    setUploading(true);
    try {
      const compressed = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const MAX = 1200;
            let { width, height } = img;
            if (width > MAX || height > MAX) {
              if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
              else { width = Math.round(width * MAX / height); height = MAX; }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width; canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', 0.75));
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      });
      const url = await uploadImage(compressed);
      setUploading(false);
      return url;
    } catch {
      setUploading(false);
      return '';
    }
  };

  const handleFullPhoto = async (e) => {
    const url = await compressAndUpload(e.target.files[0]);
    if (url) setFullPhoto(url);
    e.target.value = '';
  };

  const handleSealPhoto = async (e) => {
    const url = await compressAndUpload(e.target.files[0]);
    if (url) setSealPhoto(url);
    e.target.value = '';
  };

  const canComplete = sealNumber.trim() && sealPhoto && fullPhoto && !uploading;

  const completeLoading = () => {
    if (!canComplete) return;

    const updated = {
      ...container,
      sealNumber: sealNumber.trim(),
      sealPhotos: [sealPhoto],
      fullContainerPhoto: fullPhoto,
      loadingCompletedAt: new Date().toISOString(),
      status: 'sealed',
    };

    // Update all units to in_transit via central function
    units.forEach(unit => {
      centralUpdateStatus(unit.id, detectUnitType(unit), 'in_transit', { containerId: container.id, updatedBy: user.name });
    });

    const containers = DB.get('containers') || [];
    DB.set('containers', containers.map(c => c.id === container.id ? updated : c));

    logActivity.containerLoaded?.(container.number || container.id, units.length, user.name);

    const customerIds = [...new Set(units.map(u => u.assignedTo).filter(Boolean))];
    customerIds.forEach(custId => {
      const cust = (DB.get('customers') || []).find(c => c.id === custId);
      if (cust) {
        logActivity.containerShipped?.(`Container ${container.number || 'New'} sealed`, cust.name, user.name, custId);
      }
    });

    // Notify accountant to price the units
    try {
      const unpricedCount = units.filter(u => !u.priceJPY).length;
      Notifications.add('container_loaded', 'Container Ready for Pricing',
        `Container ${container.number || ''} sealed with ${units.length} units${unpricedCount > 0 ? ` — ${unpricedCount} need pricing` : ''}. Go to Pricing → Spreadsheet to add prices.`, {});
    } catch {}

    // ── Generate container invoices per customer ──
    generateContainerInvoices(updated, units, user);

    onUpdate(updated);
  };

  return (
    <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--green)', marginBottom: 12 }}>
        ✅ All Units Loaded — Seal & Complete
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {/* Full container photo */}
        <div>
          <label className="form-label">Full Container Photo <span className="req">*</span></label>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>Doors open, all units visible</div>
          {fullPhoto ? (
            <div style={{ position: 'relative' }}>
              <img src={fullPhoto} alt="" style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }} />
              <button onClick={() => setFullPhoto('')} style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer' }}>×</button>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" style={{ width: '100%' }} onClick={() => fullRef.current?.click()}>
              📷 Upload Photo
            </button>
          )}
          <input ref={fullRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleFullPhoto} />
        </div>

        {/* Sealed photo */}
        <div>
          <label className="form-label">Sealed Container Photo <span className="req">*</span></label>
          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 6 }}>Seal number visible</div>
          {sealPhoto ? (
            <div style={{ position: 'relative' }}>
              <img src={sealPhoto} alt="" style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 8 }} />
              <button onClick={() => setSealPhoto('')} style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: 'var(--red)', border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer' }}>×</button>
            </div>
          ) : (
            <button type="button" className="btn btn-secondary btn-sm" style={{ width: '100%' }} onClick={() => sealRef.current?.click()}>
              📷 Upload Photo
            </button>
          )}
          <input ref={sealRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleSealPhoto} />
        </div>
      </div>

      {/* Seal number */}
      <div className="form-group" style={{ marginBottom: 14 }}>
        <label className="form-label">Seal Number <span className="req">*</span></label>
        <input className="form-input" value={sealNumber} onChange={e => setSealNumber(e.target.value)}
          placeholder="e.g. SL-98765" style={{ maxWidth: 250 }} />
      </div>

      {uploading && <div style={{ fontSize: 12, color: 'var(--blue)', marginBottom: 10 }}>Uploading photo...</div>}

      {!canComplete && !uploading && (
        <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 10 }}>
          {!fullPhoto && '• Upload full container photo  '}
          {!sealPhoto && '• Upload sealed container photo  '}
          {!sealNumber.trim() && '• Enter seal number'}
        </div>
      )}

      <button className="btn btn-success" disabled={!canComplete} onClick={completeLoading}>
        <CheckIcon />Complete Loading & Seal
      </button>
    </div>
  );
}

// ── Container invoice generation ─────────────────────────────
async function generateContainerInvoices(container, units, user) {
  const invoices = DB.get('invoices') || [];
  const customers = DB.get('customers') || [];

  // Group units by customer
  const byCustomer = {};
  units.forEach(u => {
    if (!u.assignedTo) return;
    if (!byCustomer[u.assignedTo]) byCustomer[u.assignedTo] = [];
    byCustomer[u.assignedTo].push(u);
  });

  // Container total freight (split equally among all units)
  const totalFreightJPY = calculateContainerTotalJPY(container.costs);
  const freightPerUnit = units.length > 0
    ? Math.round(convertCurrency(totalFreightJPY / units.length, 'JPY', 'USD') * 100) / 100
    : 0;

  // Fee defaults
  let fees = {};
  try {
    const cfg = DB.get('appConfig');
    if (cfg && typeof cfg === 'object' && !Array.isArray(cfg)) fees = cfg.fee_defaults || {};
  } catch {}

  const newInvoices = [];

  for (const [custId, custUnits] of Object.entries(byCustomer)) {
    // Check if invoice already exists for this customer + container
    const exists = invoices.some(i =>
      i.containerId === container.id && i.customerId === custId && i.invoiceCategory === 'container'
    );
    if (exists) continue;

    const customer = customers.find(c => c.id === custId);
    if (!customer) continue;

    const invoiceNumber = await DB.nextInvoiceNumberAsync();

    // Build line items
    const lineItems = custUnits.map(u => {
      const name = u.make ? `${u.make} ${u.model}` : u.engineName || u.partName || 'Unit';
      const fob = u.priceJPY ? convertCurrency(u.priceJPY, 'JPY', 'USD') : (u.pricing?.fob_price_usd || 0);
      const flatPct = fees.flat_charge_pct || 0;
      const flatAmt = Math.round(fob * flatPct / 100 * 100) / 100;
      const recyclePct = fees.recycle_fee_pct || 0;
      const recycleAmt = Math.round(fob * recyclePct / 100 * 100) / 100;
      const vatPct = fees.vat_pct || 0;
      const vatAmt = Math.round(fob * vatPct / 100 * 100) / 100;

      return {
        unit_id: u.id, unit_sku: u.sku,
        description: `${name}${u.year ? ` (${u.year})` : ''}`,
        fob_price: Math.round(fob * 100) / 100,
        freight_share: freightPerUnit,
        fees: { flat_charge_pct: flatPct, flat_charge_amount: flatAmt, recycle_fee_pct: recyclePct, recycle_fee_amount: recycleAmt, vat_pct: vatPct, vat_amount: vatAmt },
        total: Math.round((fob + flatAmt + recycleAmt + vatAmt + freightPerUnit) * 100) / 100,
      };
    });

    const subtotal = lineItems.reduce((s, li) => s + (li.fob_price || 0), 0);
    const totalFees = lineItems.reduce((s, li) => s + ((li.fees?.flat_charge_amount || 0) + (li.fees?.recycle_fee_amount || 0) + (li.fees?.vat_amount || 0)), 0);
    const totalFreightShare = lineItems.reduce((s, li) => s + (li.freight_share || 0), 0);
    const total = Math.round((subtotal + totalFees + totalFreightShare) * 100) / 100;

    // Payment milestones from customer terms
    const terms = customer?.paymentTerms?.milestones || customer?.payment_terms?.milestones || [
      { id: 'first_payment', label: 'Before shipping', percentage: 50 },
      { id: 'final_payment', label: 'Before BL surrender', percentage: 50 },
    ];
    const today = new Date();
    const paymentMilestones = terms.map((m, i) => {
      const amount = Math.round(total * (m.percentage || 0) / 100 * 100) / 100;
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + (i === 0 ? 7 : 30 * (i + 1)));
      return {
        milestone: m.id || `ms_${i}`, label: m.label, percentage: m.percentage,
        amount, status: 'pending', due_date: dueDate.toISOString().split('T')[0],
      };
    });

    const invoice = {
      id: DB.genId('INV'),
      invoiceNumber,
      customerId: custId,
      customerName: customer.name,
      invoiceType: 'sale',
      invoiceCategory: 'container',
      containerId: container.id,
      containerNumber: container.number || '',
      lineItems,
      subtotal: Math.round(subtotal * 100) / 100,
      tax: Math.round(totalFees * 100) / 100,
      total,
      currency: 'USD',
      customerCurrency: customer.currency || 'USD',
      status: 'draft',
      issuedDate: new Date().toISOString().split('T')[0],
      amountPaid: 0,
      balanceDue: total,
      paymentMilestones,
      createdBy: user.id,
      createdAt: new Date().toISOString().split('T')[0],
    };

    newInvoices.push(invoice);
  }

  if (newInvoices.length > 0) {
    DB.set('invoices', [...invoices, ...newInvoices]);

    // Notify accountant
    try {
      Notifications.add('invoice_created', 'Container Invoices Generated',
        `Container ${container.number || ''} sealed — ${newInvoices.length} invoice${newInvoices.length !== 1 ? 's' : ''} generated. Review before sending.`, {});
    } catch {}
  }
}

export default ContainerSeal;
